import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Form,
    Input,
    Button,
    Select,
    Table,
    Row,
    Col,
    Modal,
    notification,
    Tooltip,
} from "antd";
import { CloseCircleOutlined, DeleteOutlined } from "@ant-design/icons";
import styled from "styled-components";
import AddResourceModal from "./Addresourcemodal";
import AddSubcontractorModal from "./Addsubcontractormodal";
import EditMatterResourceModal from "./EditmatterResourceModal";
import { CALL_API, CALL_FEATCH_API } from "common/API";
import { useSelector } from "react-redux";
import { getUserInfo } from "redux/selectors";
import { useFetch } from "hooks/useFetch";
import queryKeys from "common/queryKey";
import { useUpdate } from "hooks/useUpdate";
import { useScrollFetch2 } from "hooks/useScrollFetch2";
import moment from "moment";

const { Option } = Select;

const FormBody = styled.div`
  padding-bottom: 80px;

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 16px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: #333;
      .required {
        color: red;
        margin-left: 2px;
      }
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 34px !important;
    font-size: 14px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
  }

  .ant-select-selector {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 14px !important;
    line-height: 32px !important;
  }
           
  .add-btn{
    background: #00685F !important;
    color: #fff !important;
  }
`;

const StyleTable = styled(Table)`
  .ant-table-thead > tr > th {
    background-color: #dbdbdb;
    font-weight: 700;
  }
  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #dbdbdb;
    font-size: 16px !important;

  }
  .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > thead
    > tr
    > th {
    border-right: 1px solid #dbdbdb;
  }
  .ant-table.ant-table-bordered
    > .ant-table-container
    > .ant-table-content
    > table
    > tbody
    > tr
    > td {
    border-right: 1px solid #dbdbdb;
  }
  .ant-table-cell {
    padding: 6px 12px;
    font-size: 14px;
    font-weight: 500;
  }
  .ant-table {
    border-radius: 5px;
  }
  .align-right {
    text-align: right;
  }
  &.summary-table-full {
    height: 100%;
  }
  &.summary-table-full .ant-table {
    height: 100%;
  }
  &.summary-table-full .ant-table-container {
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  &.summary-table-full .ant-table-content {
    flex: 1;
  }
  &.summary-table-full table {
    height: 100%;
  }
  &.summary-table-full .ant-table-tbody {
    height: 100%;
  }
`;

const TableWrap = styled.div`
  .ant-table {
    border: 1px solid #e5e5e5 !important;
    border-radius: 0 !important;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background: #e8e8e8 !important;
    font-weight: 700 !important;
    font-size: 14px !important;
    color: #333 !important;
    padding: 12px 14px !important;
    border-bottom: 1px solid #d0d0d0 !important;
    border-radius: 0 !important;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }
  .ant-table-tbody > tr > td {
    font-size: 14px !important;
    color: #333 !important;
    padding: 12px 14px !important;
    border-bottom: 1px solid #f0f0f0 !important;
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: none !important;
  }
  .ant-table-tbody > tr:hover > td {
    background: #fafafa !important;
  }
`;

const finLabelStyle = {
    width: "185px",
    minWidth: "185px",
    display: "inline-block",
    fontWeight: 500,
    fontSize: "14px",
    color: "#444",
};

const generateSchedule = (startDate, endDate) => {
    if (!moment(startDate).isValid() || !moment(endDate).isValid()) return [];
    const schedule = [];
    let current = moment(startDate);
    const end = moment(endDate);
    while (current.isSameOrBefore(end, "day")) {
        const day = current.day();
        if (day !== 0 && day !== 6) schedule.push(current.clone());
        current.add(1, "day");
    }
    return schedule;
};


const FinancialResourcePlanFieldsForMatter = ({
    form,
    resourceList,
    setResourceList,
    subcontractorList,
    setSubcontractorList,
    onNavigateToAddResources,
    matterData,
    isEditMode,
    parentForm,
    billingEntityList = [],
    onChargeoutChange,
    onSubcontractorChange,
    onUnassignedKeysChange,
}) => {
    const [budjetChargeout, setBudjetChargeout] = useState(0);
    const [plannedSubcontractor, setPlannedSubcontractor] = useState(0);
    const [profitLoss, setProfitLoss] = useState(0);
    const [addResOpen, setAddResOpen] = useState(false);
    const [addSubOpen, setAddSubOpen] = useState(false);
    const [selectedRes, setSelectedRes] = useState(null);
    const [editingResource, setEditingResource] = useState(null);
    const [planStartDate, setPlanStartDate] = useState(null);
    const [planEndDate, setPlanEndDate] = useState(null);
    const [currencyList, setCurrencyList] = useState([]);
    const [currencyRateMap, setCurrencyRateMap] = useState({});

    const userInfo = useSelector(getUserInfo);
    const leftColRef = useRef(null);
    const [leftColHeight, setLeftColHeight] = useState(0);

    const fmtUSD = (val) =>
        val !== undefined && val !== null && val !== ""
            ? `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : `$0.00`;

    useEffect(() => {
        if (!leftColRef.current) return;
        const observer = new ResizeObserver(() => {
            setLeftColHeight(leftColRef.current?.offsetHeight || 0);
        });
        observer.observe(leftColRef.current);
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const currentYear = moment().year().toString();
        form.setFieldsValue({
            financial_year: currentYear,
        });
    }, []);

    useEffect(() => {
        sessionStorage.removeItem("pendingResources");
    }, []);

    const convertToUSD = (amount, conversionRate) => {
        const amt = parseFloat(amount || 0);
        const rate = parseFloat(conversionRate || 0);
        if (!amt || !rate) {
            form.setFieldsValue({ usd_amount: "0.00" });
            return 0;
        }
        const usd = (amt * rate).toFixed(2);
        form.setFieldsValue({ usd_amount: usd });
        return parseFloat(usd);
    };

    const profitCals = (chargeOverride, subOverride) => {
        const rawUsd = form.getFieldValue("usd_amount");
        const usdAmount = parseFloat(
            typeof rawUsd === "string" ? rawUsd.replace(/,/g, "") : rawUsd || 0
        );
        const charge = parseFloat(chargeOverride ?? budjetChargeout ?? 0);
        const sub = parseFloat(subOverride ?? plannedSubcontractor ?? 0);

        if (usdAmount === 0 && charge === 0 && sub === 0) {
            return 0;
        }

        const pl = usdAmount - (charge + sub);

        form.setFieldsValue({
            planned_fee_adjustment: pl < 0 ? Math.abs(pl).toFixed(2) : "0.00",
            pfa_usd: pl < 0 ? Math.abs(pl).toFixed(2) : "0.00",
            pfa_status: pl < 0 ? "Not Approved" : "Not Applicable",
        });

        setProfitLoss(pl);
        return pl;
    };

    const canEditMatter = () => {
        const currentUser = userInfo?.userid;
        if (!matterData || !currentUser) return false;
        const isCreator = matterData.createdBy === currentUser;
        const roles = matterData.employeeList || [];
        const userRoles = roles
            .filter(r => r?.userId === currentUser)
            .map(r => r?.projectRole);
        const isLeadAttorney = userRoles.includes("Lead Attorney");
        const isReferringAttorney = userRoles.includes("Referring Attorney");
        return isCreator || isLeadAttorney || isReferringAttorney;
    };

    const handleCurrencyChange = (value) => {
        const rate = currencyRateMap[value];
        if (rate === undefined || rate === null) return;

        form.setFieldsValue({ conversion_rate: rate });
        convertToUSD(form.getFieldValue("billing_amount"), rate);
        profitCals();
    };

    const handleBillingAmountChange = (e) => {
        const value = e.target.value;
        form.setFieldsValue({ billing_amount: value });
        setTimeout(() => {
            convertToUSD(value, form.getFieldValue("conversion_rate"));
            profitCals();
        }, 300);
    };

    const handleConversionRateChange = (e) => {
        const value = e.target.value;
        form.setFieldsValue({ conversion_rate: value });
        setTimeout(() => {
            convertToUSD(form.getFieldValue("billing_amount"), value);
            profitCals();
        }, 300);
    };

    useEffect(() => {
        const fetchCurrency = async () => {
            try {
                const res = await CALL_FEATCH_API("insyts/timesheet/currencylist", "GET");
                const data = res?.response?.data || [];
                if (data && data.length > 0) {
                    setCurrencyList(data[0]);
                    const rateObj = data[0];
                    setCurrencyRateMap(rateObj);

                    const currentCurrency = form.getFieldValue("currency");
                    if (!currentCurrency) {
                        const defaultKey = "us_dollar";
                        const defaultRate = rateObj[defaultKey] || 1;
                        form.setFieldsValue({
                            currency: defaultKey,
                            conversion_rate: defaultRate,
                        });
                    }
                }
            } catch (err) {
                console.error("Currency API Error", err);
            }
        };
        fetchCurrency();
    }, []);

    useEffect(() => {
        if (!form.getFieldValue("billing_amount")) return;
        profitCals();
    }, [budjetChargeout, plannedSubcontractor]);


    const queryParams = new URLSearchParams();
    queryParams.append("matterid", matterData?.matterId);
    queryParams.append(
        "managingOffice",
        userInfo?.managingoffice?.[0] || userInfo?.managingoffice || ""
    );

    const { getResourcePlanDataById, updateResource } = queryKeys;

    const { mutate: updateData } = useUpdate(
        updateResource.key,
        updateResource.url
    );

    const { key, url } = getResourcePlanDataById(
        matterData?.matterId,
        `?${queryParams.toString()}`
    );

    const { data, isLoading, refetch } = useScrollFetch2(key, {
        endPoint: url,
        queryParam: "lastEvaluatedKey",
    });

    const dataList = useMemo(() => {
        const raw =
            data?.pages?.reduce((acc, page) => {
                const list = page?.response?.data || [];
                return [...acc, ...list];
            }, []) || [];

        const seen = new Set();
        return raw;
    }, [data]);

    const resourceData = useMemo(() => {
        return dataList.map((res, index) => {
            const chargeoutrate =
                parseFloat(
                    res.resourceExtRate ||
                    res.chargeOutRate ||
                    res.extRate ||
                    res.rate ||
                    0
                ) || 0;
            const mandaysVal = parseFloat(res.personDay) || 0;
            const pco = mandaysVal * 8 * chargeoutrate;

            const parseDateStr = (val) => {
                if (!val) return null;

                if (moment.isMoment(val)) {
                    return val.isValid() ? val : null;
                }

                if (typeof val === "string") {
                    const formats = [
                        "YYYY-MM-DD",
                        "DD-MM-YYYY",
                        "YYYY/MM/DD",
                        "DD/MM/YYYY",
                        moment.ISO_8601,
                    ];

                    for (const format of formats) {
                        const m = moment(val, format, true);
                        if (m.isValid()) return m;
                    }

                    const fallback = moment(val);
                    return fallback.isValid() ? fallback : null;
                }

                return null;
            };

            return {
                key: res.resourceId || `api-${index}`,
                resourceId: res.resourceId || null,
                name: res.employeeName || "—",
                role: res.projectRole || "—",

                startDate: parseDateStr(
                    res.startDate ||
                    res.actualBookedStartDate ||
                    res.engagementStartDate ||
                    res.plannedStartDate ||
                    res.bookedStartDate
                ),

                finishDate: parseDateStr(
                    res.finishDate ||
                    res.actualBookedEndDate ||
                    res.engagementEndDate ||
                    res.plannedEndDate ||
                    res.bookedEndDate
                ),

                mandays: mandaysVal,
                chargeOut: `$${chargeoutrate}`,
                bookedHours: (mandaysVal * 8).toFixed(2),
                totalPco: `$${pco.toFixed(2)}`,
                totalPcoValue: pco,
                status: res.status || "Assigned",
                raw: res,
                isLocal: false,
            };
        });
    }, [dataList]);

    const [deletedResourceKeys, setDeletedResourceKeys] = useState(new Set());

    const ROLE_ORDER = { "Lead Attorney": 0, "Referring Attorney": 1 };
    const sortByRole = (data) =>
        [...data].sort((a, b) => {
            const ra = ROLE_ORDER[a.role] ?? 2;
            const rb = ROLE_ORDER[b.role] ?? 2;
            return ra - rb;
        });

    const finalResourceData = useMemo(() => {
        const extractChargeRate = (r) =>
            parseFloat(
                String(
                    r.chargeOut ||
                    r.resourceExtRate ||
                    r.raw?.resourceExtRate ||
                    r.raw?.chargeOutRate ||
                    r.chargeOutRate ||
                    "0"
                ).replace(/[^0-9.]/g, "")
            ) || 0;

        if (isEditMode) {
            const filteredApiData = (resourceData || []).map((apiRow) => {
                const resourceId = apiRow.resourceId || apiRow.raw?.resourceId;
                if (resourceId && deletedResourceKeys.has(resourceId)) {
                    return {
                        ...apiRow,
                        status: "Un Assigned",
                        raw: { ...apiRow.raw, status: "Un Assigned" },
                    };
                }
                return apiRow;
            });

            const localMap = new Map();
            (resourceList || []).forEach((r) => {
                const uid = r.raw?.userId || r.userId;
                const matchRole = r.originalRole || r.raw?.originalRole || r.raw?.projectRole || r.role;
                localMap.set(`${uid}_${matchRole}`, r);
            });

            const renderedLocalKeys = new Set();
            const renderedApiKeys = new Set();

            const merged = filteredApiData.map((apiRow) => {
                const apiUserId = apiRow.raw?.userId;
                const apiRole = apiRow.raw?.projectRole || apiRow.role;
                const apiKey = `${apiUserId}_${apiRole}`;

                const localEntry = localMap.get(apiKey);

                if (localEntry) {
                    renderedLocalKeys.add(apiKey);
                    renderedApiKeys.add(apiKey);
                    const newRole = localEntry.raw?.projectRole || localEntry.role;
                    const chargeoutrate = extractChargeRate(localEntry);
                    const mandaysVal =
                        localEntry.mandays !== undefined && localEntry.mandays !== null
                            ? parseFloat(localEntry.mandays) || 0
                            : apiRow.mandays ?? 0;
                    const pco = mandaysVal * 8 * chargeoutrate;
                    return {
                        ...apiRow,
                        resourceId: apiRow?.resourceId || null,
                        role: newRole,
                        mandays: mandaysVal,
                        chargeOut: `$${chargeoutrate}`,
                        startDate: localEntry.bookingReset ? null : (localEntry.startDate ? moment(localEntry.startDate) : apiRow.startDate),
                        finishDate: localEntry.bookingReset ? null : (localEntry.finishDate ? moment(localEntry.finishDate) : apiRow.finishDate),
                        bookedHours: (mandaysVal * 8).toFixed(2),
                        totalPco: `$${pco.toFixed(2)}`,
                        totalPcoValue: pco,
                        status: apiRow.status === "Un Assigned" ? "Un Assigned" : (localEntry.status || apiRow.status),
                        justification: localEntry.justification || localEntry.raw?.justification || apiRow.raw?.justification || "",
                        managerStatus: apiRow?.raw?.managerStatus || null,
                        raw: {
                            ...apiRow.raw,
                            projectRole: newRole,
                            actualBookedStartDate: localEntry.bookingReset ? null : (localEntry.startDate || apiRow.raw?.actualBookedStartDate),
                            actualBookedEndDate: localEntry.bookingReset ? null : (localEntry.finishDate || apiRow.raw?.actualBookedEndDate),
                            personDay: mandaysVal,
                            resourceExtRate: chargeoutrate,
                            justification: localEntry.justification || localEntry.raw?.justification || apiRow.raw?.justification || "",
                        },
                    };
                }

                renderedApiKeys.add(apiKey);
                return apiRow;
            });

            (resourceList || []).forEach((r) => {
                const uid = r.raw?.userId || r.userId;
                const matchRole = r.originalRole || r.raw?.originalRole || r.raw?.projectRole || r.role;
                const localKey = `${uid}_${matchRole}`;
                if (renderedLocalKeys.has(localKey)) return;
                const chargeoutrate = extractChargeRate(r);
                const mandaysVal = parseFloat(r.mandays) || 0;
                const pco = mandaysVal * 8 * chargeoutrate;

                merged.push({
                    key: r.key || `${uid}_${matchRole}` || `local-${Date.now()}`,
                    name: r.name,
                    role: r.role,
                    startDate: r.startDate ? moment(r.startDate) : null,
                    finishDate: r.finishDate ? moment(r.finishDate) : null,
                    mandays: mandaysVal,
                    chargeOut: `$${chargeoutrate}`,
                    bookedHours: (mandaysVal * 8).toFixed(2),
                    totalPco: `$${pco.toFixed(2)}`,
                    totalPcoValue: pco,
                    status: r.status || "Assigned",
                    justification: r.justification || r.raw?.justification || "",
                    raw: {
                        ...r,
                        justification: r.justification || r.raw?.justification || "",
                    },
                    isLocal: true,
                });
            });

            return sortByRole(merged);
        }

        // Create mode
        return sortByRole((resourceList || []).map((r, i) => {
            const chargeoutrate = extractChargeRate(r); // ✅
            const mandaysVal = parseFloat(r.mandays) || 0;
            const pco = mandaysVal * 8 * chargeoutrate;
            return {
                key: r.key || r.userId || `local-${i}`,
                name: r.name,
                role: r.role,
                startDate: r.startDate ? moment(r.startDate) : null,
                finishDate: r.finishDate ? moment(r.finishDate) : null,
                mandays: mandaysVal,
                chargeOut: `$${chargeoutrate}`,
                bookedHours: (mandaysVal * 8).toFixed(2),
                totalPco: `$${pco.toFixed(2)}`,
                totalPcoValue: pco,
                status: r.status || "Assigned",
                raw: r,
                justification: r.justification || r.raw?.justification || "",
                raw: {
                    ...r,
                    justification: r.justification || r.raw?.justification || "",  // ✅
                },
            };
        }));
    }, [resourceData, resourceList, isEditMode]);

    const mergedResourceData = useMemo(() => {
        return (resourceList || []).map((r, i) => {
            const chargeoutrate = parseFloat(
                String(r.chargeOut || r.resourceExtRate || "0").replace(/[^0-9.]/g, "")
            ) || 0;
            const mandaysVal = parseFloat(r.mandays) || 0;
            const pco = mandaysVal * 8 * chargeoutrate;

            return {
                key: r.key || r.userId || `local-${i}`,
                name: r.name,
                role: r.role,
                startDate: r.startDate ? moment(r.startDate) : null,
                finishDate: r.finishDate ? moment(r.finishDate) : null,
                mandays: mandaysVal,
                chargeOut: r.chargeOut || "$0",
                bookedHours: (mandaysVal * 8).toFixed(2),
                totalPco: `$${pco.toFixed(2)}`,
                totalPcoValue: pco,
                status: r.status || "Assigned",
                raw: r,
                isLocal: !isEditMode,
            };
        });
    }, [resourceList, isEditMode]);

    const leadAttorneyName = useMemo(() => {
        const leadEntry = finalResourceData.find(r => r.role === "Lead Attorney");
        return leadEntry?.name || matterData?.stakeHolder?.leadAttorney || "";
    }, [finalResourceData, matterData]);

    useEffect(() => {
        if (!finalResourceData || finalResourceData.length === 0) {
            if (!isEditMode) {
                setBudjetChargeout(0);
                form.setFieldsValue({ mandays: "0" });
            }
            return;
        }

        const totalPCO = finalResourceData.reduce((acc, item) => {
            return acc + (Number(item.totalPcoValue) || 0);
        }, 0);

        const totalMandays = finalResourceData.reduce((acc, item) => {
            return acc + (Number(item.mandays) || 0);
        }, 0);

        setBudjetChargeout(totalPCO);
        if (onChargeoutChange) onChargeoutChange(totalPCO);

        form.setFieldsValue({
            mandays: totalMandays.toString(),
        });

        profitCals(totalPCO, plannedSubcontractor);

    }, [finalResourceData, plannedSubcontractor, isEditMode]);

    useEffect(() => {
        const totalSub =
            subcontractorList.reduce((acc, s) => {
                const amount =
                    parseFloat(String(s.amount ?? "").replace(/[^0-9.]/g, "")) || 0;
                const rate = parseFloat(s.currencyrate) || 1;
                return acc + amount * rate;
            }, 0) || 0;

        setPlannedSubcontractor(totalSub);
        if (onSubcontractorChange) onSubcontractorChange(totalSub);
        profitCals(budjetChargeout, totalSub);
    }, [subcontractorList]);

    const handleResourceAdded = (r) => {
        setResourceList((prev) => {
            // Match by userId + role (same key used in finalResourceData merge above)
            const incomingKey = `${r.raw?.userId || r.userId}_${r.raw?.projectRole || r.role}`;
            const exists = prev.find((item) => {
                const itemKey = `${item.raw?.userId || item.userId}_${item.raw?.projectRole || item.role}`;
                return itemKey === incomingKey || item.key === r.key;
            });

            if (exists) {
                return prev.map((item) => {
                    const itemKey = `${item.raw?.userId || item.userId}_${item.raw?.projectRole || item.role}`;
                    return (itemKey === incomingKey || item.key === r.key)
                        ? { ...item, ...r }
                        : item;
                });
            }

            return [...prev, { ...r, key: r.key || `local-${Date.now()}` }];
        });
    };

    const handleSubAdded = (s) =>
        setSubcontractorList((prev) => [...prev, s]);

    const handleDeleteResource = (record) => {
        const recordKey = record.key;
        const backendResourceId = record.resourceId || record.raw?.resourceId || null;

        if (backendResourceId) {
            setResourceList((prev) =>
                prev.map((r) => {
                    const rResourceId = r.resourceId || r.raw?.resourceId;
                    if (rResourceId === backendResourceId || r.key === recordKey) {
                        return {
                            ...r,
                            status: "Un Assigned",
                            raw: { ...r.raw, status: "Un Assigned" },
                        };
                    }
                    return r;
                })
            );

            if (isEditMode) {
                setDeletedResourceKeys((prev) => new Set([...prev, backendResourceId]));
                onUnassignedKeysChange?.((prev) => {
                    const entry = {
                        resourceId: backendResourceId,
                        userId: record.raw?.userId || record.userId || "",
                        projectRole: record.raw?.projectRole || record.role || "",
                    };
                    // Avoid duplicate entries for the same row
                    const already = prev.some(
                        (u) => u.resourceId === entry.resourceId ||
                            (u.userId === entry.userId && u.projectRole === entry.projectRole)
                    );
                    return already ? prev : [...prev, entry];
                });
            }
        } else {
            setResourceList((prev) => prev.filter((r) => r.key !== recordKey));
        }
    };

    const handleDeleteSub = (key) =>
        setSubcontractorList((prev) => prev.filter((s) => s.key !== key));

    const handleResourceUpdated = (updated) =>
        setResourceList((prev) => {
            // Try to find existing entry by key
            const byKey = prev.find((r) => r.key === updated.key);
            if (byKey) {
                return prev.map((r) => (r.key === updated.key ? updated : r));
            }

            // Fallback: match by userId + role (covers API-only rows not yet in resourceList)
            const updatedUserId = updated.raw?.userId || updated.userId;
            const updatedRole = updated.raw?.projectRole || updated.role;
            const byComposite = prev.find((r) => {
                const rUserId = r.raw?.userId || r.userId;
                const rRole = r.raw?.projectRole || r.role;
                return rUserId === updatedUserId && rRole === updatedRole;
            });
            if (byComposite) {
                return prev.map((r) => {
                    const rUserId = r.raw?.userId || r.userId;
                    const rRole = r.raw?.projectRole || r.role;
                    return rUserId === updatedUserId && rRole === updatedRole ? updated : r;
                });
            }

            // Not in resourceList at all (pure API row edited for first time) — insert it
            return [...prev, updated];
        });

    const normalizeDate = (d) => {
        if (!d) return null;

        // Already a moment object
        if (moment.isMoment(d)) return d.isValid() ? d : null;

        // dayjs object (from parentForm DatePicker) — convert via string
        if (typeof d === "object" && typeof d.format === "function") {
            const str = d.format("YYYY-MM-DD");
            const m = moment(str, "YYYY-MM-DD");
            return m.isValid() ? m : null;
        }

        // String like "01-04-2026" or "2026-04-01"
        if (typeof d === "string") {
            // Try DD-MM-YYYY first (format used in form display)
            const ddmm = moment(d, "DD-MM-YYYY", true);
            if (ddmm.isValid()) return ddmm;

            // Try YYYY-MM-DD
            const iso = moment(d, "YYYY-MM-DD", true);
            if (iso.isValid()) return iso;

            // Fallback
            const fallback = moment(d);
            return fallback.isValid() ? fallback : null;
        }

        return null;
    };
    const handleRowClick = (record, e) => {
        if (e?.target?.closest(".anticon")) return;
        const rawStart = parentForm?.getFieldValue("planStartDate");
        const rawEnd = parentForm?.getFieldValue("planEndDate");

        const planStart = normalizeDate(rawStart);
        const planEnd = normalizeDate(rawEnd);

        setPlanStartDate(planStart);
        setPlanEndDate(planEnd);

        const chargeOutRate =
            record.raw?.resourceExtRate ||
            parseFloat(String(record.chargeOut || "0").replace(/[^0-9.]/g, "")) ||
            0;

        const mappedRes = {
            userId: record.raw?.userId || record.userId,
            employeeId: record.raw?.employeeId,
            name: record.name,
            role: record.role,
            chargeOutRate,
            ...record.raw,
            jobTitle: record.name,
            planStartDate: planStart,
            planEndDate: planEnd,
        };

        setEditingResource({
            ...record,
            startDate: record.startDate,   // existing booked dates for pre-fill
            finishDate: record.finishDate,
            justification: record.raw?.justification || record.justification || "",
        });

        setSelectedRes(mappedRes);
        setAddResOpen(true);
    };

    const onResetResource = (record) => {
        setResourceList((prev) =>
            prev.map((r) => {
                const isSame =
                    r.key === record.key ||
                    (r.raw?.userId || r.userId) === (record.raw?.userId || record.userId);
                if (!isSame) return r;
                return {
                    ...r,
                    mandays: 0,
                    startDate: null,
                    finishDate: null,
                    bookingReset: true,
                    bookedHours: "0.00",
                    totalPcoValue: 0,
                    totalPco: "$0.00",
                    justification: "",
                    raw: {
                        ...r.raw,
                        personDay: 0,
                        actualBookedStartDate: null,
                        actualBookedEndDate: null,
                        justification: "",
                    },
                };
            })
        );
    };

    const resCols = [
        {
            title: "Name",
            dataIndex: "name",
            render: (t, record) => {
                const isActive = matterData?.matterStatus === "Active";
                return (
                    <span
                        style={{
                            cursor: "pointer",           // always pointer — modal always opens
                            color: "#1890ff",            // always blue
                        }}
                        onClick={(e) => handleRowClick(record, e)}
                    >
                        {t}
                    </span>
                );
            },
        },
        {
            title: "Role",
            dataIndex: "role",
            render: (val, record) => {
                if (record.managerStatus) {
                    return record.managerStatus;
                } else {
                    return val;
                }
            }
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            render: (val) => {
                if (!val) return "—";

                const m = moment.isMoment(val)
                    ? val
                    : moment(val, "YYYY-MM-DD");

                if (!m.isValid()) return "—";

                return m.format("DD-MM-YYYY");
            }
        },
        {
            title: "Finish Date",
            dataIndex: "finishDate",
            render: (val) => {
                if (!val) return "—";

                const m = moment.isMoment(val)
                    ? val
                    : moment(val, "YYYY-MM-DD");

                if (!m.isValid()) return "—";

                return m.format("DD-MM-YYYY");
            }
        },
        { title: "Mandays", dataIndex: "mandays" },
        {
            title: "Charge out / hr",
            dataIndex: "chargeOut",
            render: (t) => <span>{t}</span>,
        },
        {
            title: "Total Booked Hours",
            dataIndex: "bookedHours",
            render: (t) => <span>{t}</span>,
        },
        {
            title: "Total PCO",
            dataIndex: "totalPco",
            render: (t) => <span>{t}</span>,
        },
        { title: "Status", dataIndex: "status" },
        {
            title: "Action",
            key: "action",
            render: (_, record) => {
                // "Old Lead Attorney" / "Old Referring Attorney" come through as
                // record.managerStatus (see Role column render). These should always
                // show Delete, never the reset/Close icon.
                const isOldAttorney =
                    typeof record.managerStatus === "string" &&
                    record.managerStatus.trim().toLowerCase().startsWith("old");

                const isLeadOrReferring =
                    !isOldAttorney &&
                    (record.role === "Lead Attorney" ||
                        record.role === "Referring Attorney" ||
                        record.role === "Reffering Attorney");

                const hasBookingData = record.mandays > 0 || record.startDate || record.finishDate;
                const resourceStatus = record.status || record.raw?.status || "Assigned";
                if (isLeadOrReferring) {
                    if (!hasBookingData) return null;
                    return (
                        <Tooltip title="Reset booking data">
                            <CloseCircleOutlined
                                style={{ color: "#ff4d4f", fontSize: 16, cursor: "pointer" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    onResetResource(record);
                                }}
                            />
                        </Tooltip>
                    );
                }

                return (
                    <>
                        {resourceStatus === "Assigned" && (
                            <DeleteOutlined
                                style={{ color: "#dc2626", cursor: "pointer" }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteResource(record);
                                }}
                            />
                        )
                        }
                    </>
                );
            },
        },
    ];

    const subCols = [
        { title: "Subcontractor", dataIndex: "sub" },
        { title: "Description", dataIndex: "description" },
        { title: "Currency", dataIndex: "currency" },
        { title: "Amount", dataIndex: "amount" },
        {
            title: "Reference No",
            key: "refno",
            render: (_, record) => record.refNo || record.refno || "—",
        },
        { title: "Remark", dataIndex: "remark" },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <DeleteOutlined
                    style={{ color: "#dc2626", cursor: "pointer" }}
                    onClick={() => handleDeleteSub(record.key)}
                />
            ),
        },
    ];

    const totalPlannedChargeout = finalResourceData.reduce(
        (acc, r) => acc + (parseFloat(r.totalPcoValue) || 0),
        0
    );

    const summaryProfitLoss = parseFloat(
        (form.getFieldValue("usd_amount") || "0").toString().replace(/,/g, "")
    ) - (totalPlannedChargeout + plannedSubcontractor);

    const summaryDataSource = [
        {
            key: "1",
            summary: "Total Mandays",
            manday_fee: form.getFieldValue("mandays") || "0",
        },
        {
            key: "2",
            summary: "Billing Amount(USD)",
            manday_fee: `$${parseFloat(
                String(form.getFieldValue("usd_amount") || "0").replace(/,/g, "")
            ).toFixed(2)}`,
        },
        {
            key: "3",
            summary: "Planned Chargeout",
            manday_fee: `$${totalPlannedChargeout.toFixed(2)}`,
        },
        {
            key: "4",
            summary: "Planned Subcontractor",
            manday_fee: fmtUSD(plannedSubcontractor),
        },
        {
            key: "5",
            summary: "Estimated Profit",
            manday_fee: (
                <span
                    style={{
                        color: summaryProfitLoss < 0 ? "red" : "black",
                        fontWeight: 700,
                    }}
                >
                    ${summaryProfitLoss.toFixed(2)}
                </span>
            ),
        },
        {
            key: "6",
            summary: "PFA(Planned fee Adjustment)",
            manday_fee:
                summaryProfitLoss < 0
                    ? `$${Math.abs(summaryProfitLoss).toFixed(2)}`
                    : "$0.00",
        },
        {
            key: "7",
            summary: "Profit - after PFA",
            manday_fee:
                summaryProfitLoss > 0 ? `$${Math.abs(summaryProfitLoss).toFixed(2)}` : "$0.00",
        },
    ];

    const canEdit = isEditMode && canEditMatter();

    return (
        <FormBody>
            <Form form={form} layout="vertical">
                <Form.Item name="planned_fee_adjustment" hidden>
                    <Input />
                </Form.Item>
                <Form.Item name="pfa_usd" hidden>
                    <Input />
                </Form.Item>

                <Row gutter={48} style={{ alignItems: "stretch" }}>
                    {/* ── Left column: input fields ─────────────────────────────── */}

                    <Col span={12} ref={leftColRef}>
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>
                                Billing Entity{" "}
                                <span style={{ color: "red", marginLeft: 2 }}>*</span>
                            </span>
                            <div style={{ flex: 1 }}>
                                <Form.Item
                                    name="billing_entity"
                                    rules={[{ required: true, message: "Billing Entity is required" }]}
                                    style={{ marginBottom: 4 }}
                                >
                                    <Select
                                        placeholder="Select Billing Entity"
                                        style={{ width: "100%" }}
                                        onChange={(value) => {
                                            // ✅ Find the selected entity and set its tax value into form
                                            const selectedEntity = billingEntityList.find((e) => e.value === value);
                                            if (selectedEntity?.tax) {
                                                form.setFieldsValue({ tax: selectedEntity.tax });
                                            }
                                        }}
                                    >
                                        {billingEntityList.map((entity) => (
                                            <Option key={entity.value} value={entity.value}>
                                                {entity.label}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                                <Form.Item name="tax" hidden>
                                    <Input />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Financial year */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>
                                Financial Year{" "}
                                <span style={{ color: "red", marginLeft: 2 }}>*</span>
                            </span>
                            <div style={{ flex: 1 }}>
                                <Form.Item
                                    name="financial_year"
                                    rules={[{ required: true, message: "Financial Year is required" }]}
                                    style={{ marginBottom: 4 }}
                                >
                                    <Input disabled style={{ width: "100%" }} />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Mandays */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>Mandays</span>
                            <div style={{ flex: 1 }}>
                                <Form.Item name="mandays" initialValue="0" style={{ marginBottom: 4 }}>
                                    <Input disabled style={{ width: "100%" }} />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Currency */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>Currency</span>
                            <div style={{ flex: 1 }}>
                                <Form.Item name="currency" style={{ marginBottom: 4 }}>
                                    <Select
                                        showSearch
                                        style={{ width: "100%" }}
                                        onChange={handleCurrencyChange}
                                        placeholder="Select currency"
                                    >
                                        {Object.keys(currencyRateMap)
                                            .filter(
                                                (k) =>
                                                    !["id", "date", "updated_at", "updated_currency"].includes(k)
                                            )
                                            .map((k) => (
                                                <Option key={k} value={k}>
                                                    {k.replace(/_/g, " ")}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </div>
                        </div>

                        {/* Local currency amount */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>
                                Local Currency Amount{" "}
                                <span style={{ color: "red", marginLeft: 2 }}>*</span>
                            </span>
                            <div style={{ flex: 1 }}>
                                <Form.Item
                                    name="billing_amount"
                                    rules={[{ required: true, message: "Billing Amount is required" }]}
                                    style={{ marginBottom: 4 }}
                                >
                                    <Input
                                        placeholder="Enter"
                                        onChange={handleBillingAmountChange}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* Conversion rate */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>
                                Conversion rate{" "}
                                <span style={{ color: "red", marginLeft: 2 }}>*</span>
                            </span>
                            <div style={{ flex: 1 }}>
                                <Form.Item

                                    name="conversion_rate"
                                    initialValue="1"
                                    rules={[
                                        { required: true, message: "Conversion rate is required" },
                                        {
                                            validator: (_, value) =>
                                                !value || isNaN(value) || Number(value) <= 0
                                                    ? Promise.reject("Enter a valid conversion rate")
                                                    : Promise.resolve(),
                                        },
                                    ]}
                                    style={{ marginBottom: 4 }}
                                >
                                    <Input
                                        disabled
                                        onChange={handleConversionRateChange}
                                        style={{ width: "100%" }}
                                    />
                                </Form.Item>
                            </div>
                        </div>

                        {/* USD amount — read-only calculated field */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>USD Amount</span>
                            <div style={{ flex: 1 }}>
                                <Form.Item
                                    name="usd_amount"
                                    initialValue="0.00"
                                    style={{ marginBottom: 4 }}
                                >
                                    <Input disabled style={{ width: "100%" }} />
                                </Form.Item>
                            </div>
                        </div>

                        {/* PFA status — read-only calculated field */}
                        <div style={{ display: "flex", alignItems: "flex-start", marginBottom: 4 }}>
                            <span style={{ ...finLabelStyle, paddingTop: 8 }}>PFA Status</span>
                            <div style={{ flex: 1 }}>
                                <Form.Item
                                    name="pfa_status"
                                    initialValue="Not Applicable"
                                    style={{ marginBottom: 4 }}
                                >
                                    <Input disabled style={{ width: "100%" }} />
                                </Form.Item>
                            </div>
                        </div>
                    </Col>

                    {/* ── Right column: summary table ──────────────────────────── */}
                    <Col span={12}>
                        <StyleTable
                            bordered
                            className="summary-table-full"
                            components={{
                                body: {
                                    row: (props) => {
                                        const HEADER_HEIGHT = 40;
                                        const ROW_COUNT = 7;
                                        const rowH =
                                            leftColHeight > 0
                                                ? Math.floor(
                                                    (leftColHeight - HEADER_HEIGHT) / ROW_COUNT
                                                )
                                                : "auto";
                                        return (
                                            <tr {...props} style={{ ...props.style, height: rowH }} />
                                        );
                                    },
                                },
                            }}
                            columns={[
                                { title: "Summary", dataIndex: "summary", key: "summary" },
                                {
                                    title: "Budget(USD)",
                                    dataIndex: "manday_fee",
                                    key: "manday_fee",
                                    className: "align-right",
                                },
                            ]}
                            dataSource={summaryDataSource}
                            pagination={false}
                        />
                    </Col>
                </Row>
            </Form>

            {/* ── Resource + Subcontractor tables ──────────────────────────────── */}
            <div style={{ marginTop: 28 }}>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 10,
                        marginBottom: 12,
                    }}
                >
                    {/* {canEdit && */}
                    <Button
                        className="add-btn"
                        type="primary"
                        onClick={() => {
                            if (typeof onNavigateToAddResources === "function") {
                                const leadAttorneyId = form.getFieldValue("leadAttorney");
                                const referringAttorneyId = form.getFieldValue("referringAttorney");
                                const planStartDate = parentForm?.getFieldValue("planStartDate");
                                const planEndDate = parentForm?.getFieldValue("planEndDate");
                                onNavigateToAddResources({
                                    leadAttorneyId, referringAttorneyId, planStartDate,
                                    planEndDate,
                                });
                            } else {
                                console.warn("onNavigateToAddResources not provided");
                            }
                        }}
                    >
                        Add Resources
                    </Button>
                    {/* } */}
                    {/* {canEdit && */}
                    <Button className="add-btn"
                        onClick={() => setAddSubOpen(true)}>
                        Add Subcontractor
                    </Button>
                    {/* } */}
                </div>

                <TableWrap>
                    <Table
                        columns={resCols}
                        // dataSource={mergedResourceData || []}
                        dataSource={finalResourceData || []}
                        loading={isLoading}
                        pagination={false}
                        size="small"
                        locale={{
                            emptyText: (
                                <div style={{ padding: "32px 0", color: "#aaa", fontSize: 13 }}>
                                    No resources added yet
                                </div>
                            ),
                        }}
                    />
                </TableWrap>

                <div style={{ padding: "8px 0", fontWeight: 600, fontSize: 13 }}>
                    TOTAL PLANNED CHARGEOUT: ${totalPlannedChargeout.toFixed(2)}
                </div>

                <div style={{ marginTop: 16 }}>
                    <TableWrap>
                        <Table
                            columns={subCols}
                            dataSource={subcontractorList}
                            pagination={false}
                            size="small"
                            locale={{
                                emptyText: (
                                    <div
                                        style={{ padding: "32px 0", color: "#aaa", fontSize: 13 }}
                                    >
                                        No subcontractors added yet
                                    </div>
                                ),
                            }}
                        />
                    </TableWrap>
                </div>
            </div>

            {/* ── Modals ─────────────────────────────────────────────────────────── */}

            <AddResourceModal
                open={addResOpen}
                onClose={() => {
                    setAddResOpen(false);
                    setEditingResource(null);
                }}
                selectedResource={selectedRes}
                editingResource={editingResource}
                onAdd={handleResourceAdded}
                onUpdate={handleResourceUpdated}
                fetchResourceList={refetch}
                matterData={matterData}
                mode={editingResource ? "edit" : "add"}
                planStartDate={planStartDate}
                planEndDate={planEndDate}
                leadAttorneyName={leadAttorneyName}
                isActiveMatter={matterData?.matterStatus === "Active"}
            />

            <AddSubcontractorModal
                open={addSubOpen}
                onClose={() => setAddSubOpen(false)}
                onAdd={handleSubAdded}
                currencyList={currencyList}
            />
        </FormBody>
    );
};

export default FinancialResourcePlanFieldsForMatter;